import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  createSelector
} from '@reduxjs/toolkit';

import { 
  fetchCustomerPaymentMethodsGraphQL,
  addCustomerPaymentMethodGraphQL 
} from 'api/graphql/paymentMethodsGraphQL';
import { 
  parsePaymentMethodsFromGraphQL,
  parseAddPaymentMethodResponse,
  transformToAddPaymentMethodInput 
} from 'services/paymentMethodsGraphQLServices';
import showSnackbarError from 'utils/showSnackbarError';

const initialState = {
  isLoading: false,
  isLoaded: false
};

const paymentMethodsAdapter = createEntityAdapter({
  selectId: (paymentMethod) => paymentMethod.id
});

export const getPaymentMethodsGraphQL = createAsyncThunk(
  'paymentMethods/getPaymentMethodsGraphQL',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const paymentMethodsData = await fetchCustomerPaymentMethodsGraphQL();
      const paymentMethods = parsePaymentMethodsFromGraphQL(paymentMethodsData);

      return paymentMethods;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));
      return rejectWithValue(e.message);
    }
  },
  {
    // Allow refetching on each navigation to get fresh data
    // This ensures we always see the latest active status of payment methods
    condition: (_, { getState }) => {
      const { paymentMethods } = getState();
      
      // Only skip if already loading to prevent duplicate requests
      if (paymentMethods.isLoading) {
        return false;
      }

      return true;
    },
  },
);

export const addPaymentMethodGraphQL = createAsyncThunk(
  'paymentMethods/addPaymentMethodGraphQL',
  async (data, { dispatch, getState, rejectWithValue }) => {
    try {
      // Get customerId and stripeCustomerId from auth state
      const { customerId, stripeCustomerId } = getState().auth.user;
      
      if (!customerId) {
        throw new Error('Customer ID not found. Please refresh and try again.');
      }

      // if (!stripeCustomerId) {
      //   throw new Error('Stripe Customer ID not found. Please contact support.');
      // }

      // Transform frontend data to GraphQL input
      const input = transformToAddPaymentMethodInput(data, customerId, stripeCustomerId, 53, 60);

      // Call GraphQL mutation
      const paymentMethod = await addCustomerPaymentMethodGraphQL(input);

      // Parse response to frontend format
      const parsedPaymentMethod = parseAddPaymentMethodResponse(paymentMethod);

      return parsedPaymentMethod;
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Payment Method Failed';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const paymentMethodsSlice = createSlice({
  name: 'paymentMethods',
  initialState: paymentMethodsAdapter.getInitialState(initialState),
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentMethodsGraphQL.pending, (state) => {
        state.isLoading = true;
        state.isLoaded = false;
      })
      .addCase(getPaymentMethodsGraphQL.fulfilled, (state, action) => {
        // Replace all existing payment methods with fresh data to ensure
        // inactive payment methods are removed from the store
        paymentMethodsAdapter.setAll(state, action.payload);
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(getPaymentMethodsGraphQL.rejected, (state) => {
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(addPaymentMethodGraphQL.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addPaymentMethodGraphQL.fulfilled, (state, action) => {
        paymentMethodsAdapter.addOne(state, action.payload);
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(addPaymentMethodGraphQL.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

const { selectAll, selectById } = paymentMethodsAdapter.getSelectors(
  (state) => state.paymentMethods,
);

const all = createSelector(selectAll, (state) => state);

const byId = (id) => createSelector(
  (state) => state,
  (state) => selectById(state, id),
);

const selectSelf = (state) => state.paymentMethods;

const isLoading = createSelector(selectSelf, (state) => state.isLoading);
const isLoaded = createSelector(selectSelf, (state) => state.isLoaded);

export const PaymentMethodsSelectors = {
  all,
  byId,
  isLoading,
  isLoaded
};

export default paymentMethodsSlice.reducer;
